import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormatNumberOptions, IntlShape, useIntl } from "react-intl";
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs
} from "@floating-ui/react";
import { IconX, IconZoomMoney } from "@tabler/icons-react";
import { AnyPrice, classNames, useIsMounted } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { bookingTranslations } from "../../../i18n/sharable-defs";
import { AmountRange } from '../../../models/search';
import {
  MainSearchInputProps,
  SearchInputDefinitionProps,
  SearchInputSelectionProps,
  SearchInputViewProps
} from "./common";
import './pricerange-input.scss';

const transDefs = defineMessages({
  any: {id: "search-form-pricerange-input-default", defaultMessage: "N'importe quel prix" }
});

export const priceRangeFormattedState = (intl: IntlShape, printFunc: (value: [ AnyPrice, AnyPrice ], opt?: FormatNumberOptions, textual?: boolean) => string, val?: AmountRange) => {
  if (!val) return intl.formatMessage(transDefs.any);
  const range = val.map(amount => ({ amount, currency: "CHF" })) as [ AnyPrice, AnyPrice ];
  return printFunc(range, { trailingZeroDisplay: "stripIfInteger" }, true);
}

type InputExtraProps = { min: number; max: number; step?: number; };
type InputViewProps = SearchInputDefinitionProps & InputExtraProps & SearchInputViewProps<AmountRange>;
type InputSelectionProps = InputExtraProps & SearchInputSelectionProps<AmountRange>;
type MainSearchPriceRangeInputProps = MainSearchInputProps<AmountRange> & InputExtraProps;

const InputSelection = (
  {
    min,
    max,
    step,
    value,
    onSelection
  }: InputSelectionProps) => {

  const { print } = useLocaleFormatter();
  const [ minVal, setMinVal ] = useState(min);
  const [ maxVal, setMaxVal ] = useState(max);

  useEffect(() => {
    if (value) {
      setMinVal(value[0] ?? min);
      setMaxVal(value[1] ?? max);
    }
  }, [ value, min, max ]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), maxVal - step!);
    setMinVal(newMin);
    onSelection?.([ newMin, maxVal ]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), minVal + step!);
    setMaxVal(newMax);
    onSelection?.([ minVal, newMax ]);
  };

  const minPercent = ((minVal - min) / (max - min)) * 100;
  const maxPercent = ((maxVal - min) / (max - min)) * 100;

  return (
    <div className="price-range">
      <div className="price-labels">
        <span>{print.price({ amount: minVal, currency: "CHF"}, { trailingZeroDisplay: "stripIfInteger" })}</span>
        <span>{print.price({ amount: maxVal, currency: "CHF"}, { trailingZeroDisplay: "stripIfInteger" })}</span>
      </div>

      <div className="slider">
        <div className="slider-track"/>
        <div className="slider-range" style={{left: `${minPercent}%`, right: `${100 - maxPercent}%`}} />
        <input type="range"
               min={min}
               max={max}
               step={step}
               value={minVal}
               onChange={handleMinChange}
               className="thumb thumb--left" />
        <input type="range"
               min={min}
               max={max}
               step={step}
               value={maxVal}
               onChange={handleMaxChange}
               className="thumb thumb--right" />
      </div>
    </div>);

}

const InputMobileView = (
  {
    name,
    open,
    toggle,
    min,
    max,
    step,
    value,
    formattedValue,
    onValueChange
  }: InputViewProps) => {

  const intl = useIntl();

  // dom interactions

  const onSelection = (val: AmountRange | undefined, e?: MouseEvent) => {
    onValueChange(val);
    e?.stopPropagation();
  }

  const onReset = (e: MouseEvent) => {
    onValueChange(undefined);
    e.stopPropagation();
  }

  // rendering

  const wrapperClasses = classNames("input-wrap", name, { selection: open });

  const editionRendering = useMemo(() => (
    <>
      <h2 onClick={() => toggle(name, false)}>{intl.formatMessage(bookingTranslations.price)}</h2>
      <InputSelection {...{min, max, step, value, onSelection}}/>
    </>), [ value, min, max, step ]);

  const resumeRendering = useMemo(() => (
    <>
      <div className="input-label ellipsed">{intl.formatMessage(bookingTranslations.price)}</div>
      <div className={classNames("input-val-state", { empty: !value })}>
        <span className="label ellisped">{intl.formatMessage(transDefs.any)}</span>&nbsp;
        <span className="value ellipsed">{formattedValue || intl.formatMessage(transDefs.any)}</span>&nbsp;
      </div>
      {!!value &&
        <button type="button" className="resetter" onClick={onReset}><IconX/></button>
      }
    </>
  ), [value, formattedValue]);

  return (
    <div tabIndex={0}
         className={wrapperClasses} onClick={() => !open && toggle(name, true)}>
      {open ? editionRendering : resumeRendering}
    </div>);

};
const InputDesktopView = (
  {
    name,
    open,
    toggle,
    min,
    max,
    step,
    value,
    formattedValue,
    onValueChange
  }: InputViewProps) => {

  const intl = useIntl();

  // picker floating display

  const {x, y, reference, floating, strategy, context} = useFloating({
    open: open,
    onOpenChange: isOpen => toggle(name, isOpen),
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [ offset(8) ]
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, { enabled: open, ancestorScroll: true })
  ]);


  // dom interaction

  const onSelection = (val: AmountRange | undefined, _?: MouseEvent) => {
    onValueChange(val);
  }

  // rendering

  const inputRef = useMergeRefs([ reference ]);
  const inputWrapperClasses = classNames("input-wrap", name);
  const pickerClasses = classNames("rf-searcher-input-dropdown", name);

  return (
    <>
      <div className={inputWrapperClasses}>
        <div ref={inputRef}
             {...getReferenceProps()}
             tabIndex={0}
             className="input-content inner-space">
          <IconZoomMoney/>
          <div className="input-val-wrap">
            <div className="input-label ellipsed">{intl.formatMessage(bookingTranslations.price)}</div>
            <div className={classNames("input-val-state", { empty: !value })}>
              {!!formattedValue && <span className="value ellipsed">{formattedValue}</span>}
              <span className="label ellipsed">{intl.formatMessage(transDefs.any)}</span>
            </div>
          </div>
        </div>
        {!!formattedValue &&
          <button type="button" className="resetter" onClick={() => onValueChange(undefined)}><IconX/></button>
        }
      </div>
      <FloatingPortal>
        {open &&
          <FloatingFocusManager context={context}>
            <div ref={floating}
                 {...getFloatingProps()}
                 className={pickerClasses}
                 style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}>
              <InputSelection {...{ min, max, step, value, onSelection }} />
            </div>
          </FloatingFocusManager>
        }
      </FloatingPortal>
    </>);

};

const MainSearchPriceRangeInput = (
  {
    name,
    open,
    toggle,
    mobileView,
    min,
    max,
    step,
    value: initedValue,
    onValueChange
  }: MainSearchPriceRangeInputProps) => {

  const isMounted = useIsMounted();
  const intl = useIntl();
  const { print } = useLocaleFormatter();

  // component states

  const [ value, setValue ] = useState<AmountRange | undefined>(initedValue);

  useEffect(() => {
    if (!isMounted) return;
    value !== initedValue && setValue(initedValue);
  }, [ initedValue ]);

  useEffect(() => {
    if (!isMounted) return;
    onValueChange(value);
  }, [ value ]);

  const formattedValue = useMemo(() => {
    if (!value) return "";
    return priceRangeFormattedState(intl, print.priceRange, value);
  }, [ value, intl ]);

  // rendering
  if (mobileView)
    return (
      <InputMobileView name={name}
                       open={open}
                       toggle={toggle}
                       min={min}
                       max={max}
                       step={step ?? (max - min) / 10}
                       value={value}
                       formattedValue={formattedValue}
                       onValueChange={value => setValue(value)} />);

  return (
    <InputDesktopView name={name}
                      open={open}
                      toggle={toggle}
                      min={min}
                      max={max}
                      step={step ?? (max - min) / 10}
                      value={value}
                      formattedValue={formattedValue}
                      onValueChange={value => setValue(value)} />);

}

export default MainSearchPriceRangeInput;
